import { datePl, FUEL_MATCH_STATUSES, FUEL_TX_KINDS, type FuelTransactionDto, type MeDto, todayInWarsaw } from '@bqcrm/shared';
import { Alert, Badge, Button, Group, List, Modal, Select, Stack, Table, Text, TextInput } from '@mantine/core';
import { IconInfoCircle, IconPlus } from '@tabler/icons-react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { api } from '../../api';
import { CompanyBadgeById } from '../../components/Badges';
import { Money } from '../../components/Money';
import { PageHeader } from '../../components/PageHeader';
import { QueryState } from '../../components/QueryState';
import { EmptyState, SectionCard } from '../../components/ui';
import { formatDateTime, qtyPl } from '../../format';
import { notifyError, notifyOk } from '../../notify';
import { FleetShell, kmPl, useFleetInvalidate, useFleetVehicles, vehicleLabel } from './common';
import { useMyCompanies } from './VehiclesPage';

interface BatchDto {
  id: string;
  companyId: string;
  source: string;
  periodFrom: string | null;
  periodTo: string | null;
  rows: number;
  added: number;
  createdAt: string;
  matched: number;
  pending: number;
}

interface CardDto {
  id: string;
  companyId: string;
  label: string;
  cardRef: string;
  active: boolean;
  assignments: { vehicleId: string; plate: string; validFrom: string; validTo: string | null }[];
}

function ResolveModal({ tx, onClose }: { tx: FuelTransactionDto; onClose: () => void }) {
  const vehicles = useFleetVehicles();
  const invalidate = useFleetInvalidate();
  const [vehicleId, setVehicleId] = useState<string | null>(null);
  const [note, setNote] = useState('');
  const save = useMutation({
    mutationFn: (status: 'dopasowana' | 'pominieta') => api(`/fleet/import/transactions/${tx.id}/resolve`, { method: 'POST', body: { vehicleId: status === 'dopasowana' ? vehicleId : null, status, note } }),
    onSuccess: (_r, status) => {
      notifyOk(status === 'dopasowana' ? 'Przypisano transakcję do pojazdu' : 'Pominięto transakcję');
      invalidate();
      onClose();
    },
    onError: notifyError,
  });
  return (
    <Modal opened onClose={onClose} title="Wyjaśnienie transakcji paliwowej">
      <Stack gap="sm">
        <Text size="sm">
          {formatDateTime(tx.txAt)} · {FUEL_TX_KINDS[tx.kind]} {tx.productName} · <Money gr={tx.netGr} /> netto
        </Text>
        <Text size="xs" c="dimmed">
          Numer z transakcji: {tx.plate || '—'} · karta: {tx.cardRef || '—'} · {tx.matchNote}
        </Text>
        <Select label="Pojazd" searchable data={(vehicles.data ?? []).map((v) => ({ value: v.id, label: vehicleLabel(v) }))} value={vehicleId} onChange={setVehicleId} />
        <TextInput label="Notatka" value={note} onChange={(e) => setNote(e.currentTarget.value)} />
        <Group justify="flex-end">
          <Button variant="default" onClick={onClose}>
            Anuluj
          </Button>
          <Button variant="light" color="gray" loading={save.isPending && save.variables === 'pominieta'} onClick={() => save.mutate('pominieta')}>
            Pomiń transakcję
          </Button>
          <Button disabled={!vehicleId} loading={save.isPending && save.variables === 'dopasowana'} onClick={() => save.mutate('dopasowana')}>
            Przypisz do pojazdu
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
}

function CardModal({ me, onClose }: { me: MeDto; onClose: () => void }) {
  const companies = useMyCompanies(me);
  const vehicles = useFleetVehicles();
  const invalidate = useFleetInvalidate();
  const [companyId, setCompanyId] = useState<string | null>(companies.length === 1 ? companies[0]!.id : null);
  const [label, setLabel] = useState('');
  const [cardRef, setCardRef] = useState('');
  const [vehicleId, setVehicleId] = useState<string | null>(null);
  const [validFrom, setValidFrom] = useState(todayInWarsaw());
  const fullNumber = /\d{12,}/.test(cardRef.replace(/\s/g, ''));
  const save = useMutation({
    mutationFn: () => api('/fleet/import/cards', { method: 'POST', body: { companyId, label: label.trim(), cardRef: cardRef.trim(), vehicleId, validFrom } }),
    onSuccess: () => {
      notifyOk('Dodano kartę paliwową');
      invalidate();
      onClose();
    },
    onError: notifyError,
  });
  return (
    <Modal opened onClose={onClose} title="Nowa karta paliwowa">
      <Stack gap="sm">
        <Alert color="yellow" variant="light" py="xs">
          Nie wpisuj pełnego numeru karty — wystarczą ostatnie cyfry albo numer wewnętrzny karty z portalu.
        </Alert>
        <Select label="Firma" required data={companies.map((c) => ({ value: c.id, label: c.shortName }))} value={companyId} onChange={setCompanyId} />
        <TextInput label="Nazwa karty" required placeholder="np. Karta — Ekipa 1" value={label} onChange={(e) => setLabel(e.currentTarget.value)} />
        <TextInput label="Identyfikator karty" required placeholder="np. …4821" value={cardRef} onChange={(e) => setCardRef(e.currentTarget.value)} error={fullNumber ? 'To wygląda na pełny numer karty — wpisz tylko ostatnie cyfry' : undefined} />
        <Select label="Pojazd (opcjonalnie)" clearable searchable data={(vehicles.data ?? []).filter((v) => !companyId || v.companyId === companyId).map((v) => ({ value: v.id, label: vehicleLabel(v) }))} value={vehicleId} onChange={setVehicleId} />
        {vehicleId && <TextInput label="Przypisana od" type="date" value={validFrom} onChange={(e) => setValidFrom(e.currentTarget.value)} />}
        <Group justify="flex-end">
          <Button variant="default" onClick={onClose}>
            Anuluj
          </Button>
          <Button disabled={!companyId || !label.trim() || cardRef.trim().length < 2 || fullNumber} loading={save.isPending} onClick={() => save.mutate()}>
            Dodaj kartę
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
}

/** Import paliwa (ORLEN Flota): co jest potrzebne do importu, partie, kolejka do wyjaśnienia i karty paliwowe. */
export function FleetImportPage({ me }: { me: MeDto }) {
  const batches = useQuery({ queryKey: ['fleet', 'import', 'batches'], queryFn: () => api<BatchDto[]>('/fleet/import/batches') });
  const queue = useQuery({ queryKey: ['fleet', 'import', 'queue'], queryFn: () => api<FuelTransactionDto[]>('/fleet/import/transactions?status=do_wyjasnienia') });
  const cards = useQuery({ queryKey: ['fleet', 'import', 'cards'], queryFn: () => api<CardDto[]>('/fleet/import/cards') });
  const [resolving, setResolving] = useState<FuelTransactionDto | null>(null);
  const [addingCard, setAddingCard] = useState(false);
  return (
    <FleetShell me={me}>
      <PageHeader title="Import paliwa" description="Transakcje z kart paliwowych ORLEN Flota: dopasowanie do pojazdów po numerze rejestracyjnym i karcie, kolejka do wyjaśnienia i uzgodnienie z fakturą zbiorczą." />
      <Stack gap="md">
        <Alert color="blue" icon={<IconInfoCircle size={18} />} title="Wczytywanie plików ORLEN — po otrzymaniu próbek">
          <Text size="sm" mb={6}>
            Model danych, kolejka do wyjaśnienia i rozdzielanie faktury na pojazdy są gotowe. Nie znamy jeszcze dokładnego formatu danych ORLEN, dlatego wczytywanie pliku zostanie dodane po otrzymaniu próbek. Potrzebne od firmy:
          </Text>
          <List size="sm" spacing={4} type="ordered">
            <List.Item>
              <b>Jedna faktura ORLEN w formacie XML z KSeF</b> (FA(3)) — najlepiej za pełny okres rozliczeniowy z kilkoma kartami: Finanse → Faktury zakupu → faktura ORLEN → „Pobierz XML z KSeF”. Plus jedna <b>korekta</b> ORLEN, jeśli była.
            </List.Item>
            <List.Item>
              <b>Zestawienie transakcji z portalu ORLEN Flota</b> za ten sam okres, w formacie, który eksportuje portal (CSV / XLSX / PDF), z nagłówkami kolumn — bez przerabiania w Excelu.
            </List.Item>
            <List.Item>
              Informacja, czy kierowcy wpisują <b>stan licznika</b> przy płaceniu kartą i jak wyglądają identyfikatory kart (przypisane do numeru rejestracyjnego czy do kierowcy).
            </List.Item>
            <List.Item>Bezpośrednia integracja — dopiero po otrzymaniu od ORLEN dokumentacji API i warunków dostępu.</List.Item>
          </List>
          <Text size="xs" c="dimmed" mt={6}>
            Pełne XML faktur są przechowywane, więc po poznaniu struktury odczytamy także faktury już pobrane.
          </Text>
        </Alert>

        <SectionCard title={`Do wyjaśnienia${queue.data?.length ? ` (${queue.data.length})` : ''}`} padding={0}>
          <QueryState isLoading={queue.isLoading} error={queue.error}>
            {queue.data?.length ? (
              <Table.ScrollContainer minWidth={900}>
                <Table highlightOnHover>
                  <Table.Thead>
                    <Table.Tr>
                      <Table.Th>Data</Table.Th>
                      <Table.Th>Numer / karta</Table.Th>
                      <Table.Th>Zakup</Table.Th>
                      <Table.Th ta="right">Netto</Table.Th>
                      <Table.Th>Stacja</Table.Th>
                      <Table.Th>Powód</Table.Th>
                      <Table.Th />
                    </Table.Tr>
                  </Table.Thead>
                  <Table.Tbody>
                    {queue.data.map((t) => (
                      <Table.Tr key={t.id}>
                        <Table.Td>{formatDateTime(t.txAt)}</Table.Td>
                        <Table.Td>
                          <Text size="sm">{t.plate || '—'}</Text>
                          <Text size="xs" c="dimmed">
                            karta {t.cardRef || '—'}
                          </Text>
                        </Table.Td>
                        <Table.Td>
                          <Text size="sm">
                            {FUEL_TX_KINDS[t.kind]}
                            {t.quantity !== null ? ` · ${qtyPl(t.quantity)} ${t.unit}` : ''}
                          </Text>
                          <Text size="xs" c="dimmed">
                            {t.productName}
                            {t.km !== null ? ` · ${kmPl(t.km)}` : ''}
                          </Text>
                        </Table.Td>
                        <Table.Td ta="right">
                          <Money gr={t.netGr} />
                        </Table.Td>
                        <Table.Td>
                          <Text size="sm">{t.station || '—'}</Text>
                        </Table.Td>
                        <Table.Td>
                          <Badge size="sm" variant="light" color={t.matchStatus === 'niejednoznaczna' ? 'orange' : 'yellow'}>
                            {FUEL_MATCH_STATUSES[t.matchStatus]}
                          </Badge>
                          <Text size="xs" c="dimmed">
                            {t.matchNote}
                          </Text>
                        </Table.Td>
                        <Table.Td ta="right">
                          <Button size="compact-sm" variant="light" onClick={() => setResolving(t)}>
                            Wyjaśnij
                          </Button>
                        </Table.Td>
                      </Table.Tr>
                    ))}
                  </Table.Tbody>
                </Table>
              </Table.ScrollContainer>
            ) : (
              <EmptyState compact title="Nic do wyjaśnienia" hint="Transakcje bez rozpoznanego pojazdu pojawią się tu po imporcie." />
            )}
          </QueryState>
        </SectionCard>

        <SectionCard title="Partie importu" padding={0}>
          <QueryState isLoading={batches.isLoading} error={batches.error}>
            {batches.data?.length ? (
              <Table.ScrollContainer minWidth={700}>
                <Table highlightOnHover>
                  <Table.Thead>
                    <Table.Tr>
                      <Table.Th>Wczytano</Table.Th>
                      <Table.Th>Firma</Table.Th>
                      <Table.Th>Okres</Table.Th>
                      <Table.Th ta="right">Wiersze</Table.Th>
                      <Table.Th ta="right">Nowe</Table.Th>
                      <Table.Th ta="right">Dopasowane</Table.Th>
                      <Table.Th ta="right">Do wyjaśnienia</Table.Th>
                    </Table.Tr>
                  </Table.Thead>
                  <Table.Tbody>
                    {batches.data.map((b) => (
                      <Table.Tr key={b.id}>
                        <Table.Td>{formatDateTime(b.createdAt)}</Table.Td>
                        <Table.Td>
                          <CompanyBadgeById id={b.companyId} />
                        </Table.Td>
                        <Table.Td>{b.periodFrom || b.periodTo ? `${datePl(b.periodFrom)} – ${datePl(b.periodTo)}` : '—'}</Table.Td>
                        <Table.Td ta="right">{b.rows}</Table.Td>
                        <Table.Td ta="right">{b.added}</Table.Td>
                        <Table.Td ta="right">{b.matched}</Table.Td>
                        <Table.Td ta="right">
                          {b.pending ? (
                            <Badge size="sm" color="yellow" variant="light">
                              {b.pending}
                            </Badge>
                          ) : (
                            '—'
                          )}
                        </Table.Td>
                      </Table.Tr>
                    ))}
                  </Table.Tbody>
                </Table>
              </Table.ScrollContainer>
            ) : (
              <EmptyState compact title="Nie było jeszcze importu" />
            )}
          </QueryState>
        </SectionCard>

        <SectionCard
          title="Karty paliwowe"
          padding={0}
          actions={
            <Button size="xs" leftSection={<IconPlus size={14} />} onClick={() => setAddingCard(true)}>
              Dodaj kartę
            </Button>
          }
        >
          <QueryState isLoading={cards.isLoading} error={cards.error}>
            {cards.data?.length ? (
              <Table.ScrollContainer minWidth={600}>
                <Table highlightOnHover>
                  <Table.Thead>
                    <Table.Tr>
                      <Table.Th>Karta</Table.Th>
                      <Table.Th>Firma</Table.Th>
                      <Table.Th>Pojazd</Table.Th>
                    </Table.Tr>
                  </Table.Thead>
                  <Table.Tbody>
                    {cards.data.map((c) => {
                      const cur = c.assignments.find((a) => !a.validTo) ?? c.assignments[c.assignments.length - 1];
                      return (
                        <Table.Tr key={c.id} style={!c.active ? { opacity: 0.55 } : undefined}>
                          <Table.Td>
                            <Text size="sm" fw={500}>
                              {c.label}
                            </Text>
                            <Text size="xs" c="dimmed">
                              {c.cardRef}
                            </Text>
                          </Table.Td>
                          <Table.Td>
                            <CompanyBadgeById id={c.companyId} />
                          </Table.Td>
                          <Table.Td>
                            {cur ? (
                              <Text size="sm">
                                {cur.plate}{' '}
                                <Text span size="xs" c="dimmed">
                                  od {datePl(cur.validFrom)}
                                  {cur.validTo ? ` do ${datePl(cur.validTo)}` : ''}
                                </Text>
                              </Text>
                            ) : (
                              <Text size="sm" c="dimmed">
                                bez pojazdu
                              </Text>
                            )}
                          </Table.Td>
                        </Table.Tr>
                      );
                    })}
                  </Table.Tbody>
                </Table>
              </Table.ScrollContainer>
            ) : (
              <EmptyState compact title="Brak kart paliwowych" hint="Karta przypisana do pojazdu pozwala dopasować transakcje bez numeru rejestracyjnego." />
            )}
          </QueryState>
        </SectionCard>
      </Stack>
      {resolving && <ResolveModal tx={resolving} onClose={() => setResolving(null)} />}
      {addingCard && <CardModal me={me} onClose={() => setAddingCard(false)} />}
    </FleetShell>
  );
}
